import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { impersonationService, ImpersonationStatus } from '../services/impersonationService';

interface ImpersonationContextType {
  isImpersonating: boolean;
  tenantName: string | null;
  stopImpersonation: () => Promise<void>;
  loading: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<ImpersonationStatus>({ is_impersonating: false });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStatus = async () => {
    try {
      const data = await impersonationService.getImpersonationStatus();
      setStatus(data);
    } catch {
      setStatus({ is_impersonating: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const stopImpersonation = async () => {
    try {
      const { redirect_to } = await impersonationService.stopImpersonation();
      setStatus({ is_impersonating: false });
      navigate(redirect_to);
    } catch (e) {
      console.error('Failed to stop impersonation:', e);
    }
  };

  const value: ImpersonationContextType = {
    isImpersonating: status.is_impersonating ?? false,
    tenantName: status.tenant_name ?? null,
    stopImpersonation,
    loading,
  };

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  );
};
